
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, Battery } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatPlomb = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Plomb - Rachat Batteries, Tuyaux, Balles | Groupe Fabre"
        description="Rachat de plomb au meilleur prix : batteries, tuyauteries, balles de chasse, contrepoids. Pesée certifiée et paiement immédiat."
        canonical="/nos-services/achat-metaux/plomb"
        keywords="achat plomb, rachat batteries plomb, tuyaux plomb, prix plomb, recyclage plomb"
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <Battery className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat de Plomb</h1>
                  <p className="text-xl mt-2 opacity-90">Rachat de batteries, tuyaux et contrepoids</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat de plomb - Métal lourd valorisé
                </h2>
                <p className="text-gray-600 mb-6">
                  Le <strong>plomb</strong> est un métal lourd très recherché pour le recyclage. 
                  <strong>Groupe Fabre Environnement</strong> rachète tous types de plomb : 
                  batteries automobiles, tuyauteries anciennes, balles et contrepoids.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Types de plomb rachetés</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-600 text-white mr-3">•</span>
                    <span><strong>Batteries :</strong> automobiles, industrielles, onduleurs</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-600 text-white mr-3">•</span>
                    <span><strong>Tuyauteries :</strong> plomberie ancienne, soudures</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-600 text-white mr-3">•</span>
                    <span><strong>Contrepoids :</strong> balances, équilibrage</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-600 text-white mr-3">•</span>
                    <span><strong>Balles :</strong> chasse, tir sportif</span>
                  </li>
                </ul>

                <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                  <h4 className="font-bold text-blue-800 mb-3">⚠️ Gestion responsable</h4>
                  <ul className="text-blue-700 text-sm space-y-2">
                    <li>• Respect des normes environnementales</li>
                    <li>• Traitement sécurisé du plomb</li>
                    <li>• Recyclage sans impact sur la santé</li>
                    <li>• Certificats de traçabilité fournis</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Valorisation sécurisée</h3>
                <p className="text-gray-600 mb-6">
                  Notre expertise en gestion du plomb nous permet de vous proposer 
                  une valorisation optimale en respectant toutes les normes de sécurité.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-blue-600 mr-3" />
                    <span>Prix plomb attractifs</span>
                  </div>
                  <div className="flex items-center">
                    <Battery className="h-5 w-5 text-blue-600 mr-3" />
                    <span>Spécialiste batteries</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-blue-600 mr-3" />
                    <span>Traitement sécurisé</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors">
                    Demander prix plomb
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Types de plomb */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Classifications plomb et applications
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Plomb Pur</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Lingots, plaques, tuyaux sans alliage
                </p>
                <div className="text-green-600 font-medium">Prix le plus élevé</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Batteries Plomb</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Batteries auto, moto, industrielles
                </p>
                <div className="text-blue-600 font-medium">Prix spécifique</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Plomb d'Œuvre</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Couverture, soudures, alliages
                </p>
                <div className="text-orange-600 font-medium">Prix ajusté</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatPlomb;
